import { computed, inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { StateApiError } from '@trovata/app/core/models/state-error.model';
import { ReadyForReviewSnack } from '@trovata/app/shared/models/ready-for-review-snack.model';
import { firstValidValueFrom } from '@trovata/app/shared/utils/firstValidValueFrom';
import { concat, firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { map as rxMap } from 'rxjs/operators';
import {
	BatchPayment,
	GetPaymentsV1Response,
	InstitutionCountry,
	InstitutionAddtlInstruction,
	LexisNexisBankInformationResponse,
	Payment,
	PaymentSubmissionOrCancelation,
	PaymentToCreate,
	StageFeedback,
	PaymentsTab,
	PaymentsListQuery,
	PaymentRecord,
	isPayment,
	isBatchPayment,
	BulkApprovalRequestItem,
	BulkApprovalRequest,
	BulkApprovalResponse,
	BulkApprovalResult,
	ValidateBatchCsvFileResponse,
} from '@trovata/app/features/payments/models/payment.model';
import {
	CancelBatchPayment,
	CancelPayment,
	CreateBatchPayment,
	CreatePayment,
	GetBatchPaymentById,
	GetPaymentById,
	SetPaymentsTabIndex,
	SetStageFeedback,
	SetBatchStageFeedback,
	DeleteFileFromPayment,
	UploadFileToPayment,
	GetPaymentRecords,
	UpdatePaymentsFromBulkApproval,
} from '@trovata/app/features/payments/store/actions/payments.actions';
import { AnalyticsUserActions } from '@trovata/app/core/utils/analyticsEnums';
import { PaymentsState } from '@trovata/app/features/payments/store/state/payments/payments.state';
import { PaymentsHttpService } from '@trovata/app/features/payments/services/payments/payments-http.service';
import { VitallyService } from '@trovata/app/core/services/vitally.service';
import { InstitutionLevelInstructions, InstructionsState } from '@trovata/app/features/payments/store/state/instructions/instructions.state';
import { GetInstitutionCountries, GetInstitutionInstructions } from '@trovata/app/features/payments/store/actions/instructions.actions';
import { WorkflowsFacadeService } from '@trovata/app/features/payments/services/workflows/facade/workflows.facade.service';

/**
 * PaymentsFacadeService - Simplified API over payments NGXS state management.
 *
 * This facade encapsulates all interactions with PaymentsState, providing:
 * - Reactive signals for state access (read-only)
 * - Async methods for API operations that update state
 * - Synchronous snapshot methods for immediate state reads
 *
 * Key behaviors:
 * - All state mutations go through NGXS actions
 * - Signals provide reactive, automatically-updating state access
 * - API methods return updated data after state is patched
 */
@Injectable({
	providedIn: 'root',
})
export class PaymentsFacadeService {
	// ==========================================
	// Services (injected)
	// ==========================================

	private readonly store: Store = inject(Store);
	private readonly vitallyService: VitallyService = inject(VitallyService);
	private readonly paymentsHttpService: PaymentsHttpService = inject(PaymentsHttpService);
	private readonly workflowsFacade: WorkflowsFacadeService = inject(WorkflowsFacadeService);

	// ==========================================
	// Store Signals
	// ==========================================

	/** All payment record entities in the store */
	readonly paymentRecordEntities: Signal<PaymentRecord[]> = this.store.selectSignal(PaymentsState.paymentRecordEntities);

	/** Error state for All Payments table */
	readonly allPaymentsError: Signal<boolean> = this.store.selectSignal(PaymentsState.allPaymentsError);

	/** Error state for Ready for Review table */
	readonly reviewPaymentsError: Signal<boolean> = this.store.selectSignal(PaymentsState.reviewPaymentsError);

	/** Error state for Open Payments table */
	readonly openPaymentsError: Signal<boolean> = this.store.selectSignal(PaymentsState.openPaymentsError);

	/** Currently selected payments tab */
	readonly selectedTab: Signal<PaymentsTab> = this.store.selectSignal(PaymentsState.selectedTab);

	/** Array of API errors for the payments module */
	readonly apiErrors: Signal<StateApiError[]> = this.store.selectSignal(PaymentsState.paymentsApiErrors);

	/** Current query for the "All Payments" table */
	readonly allPaymentsTableQuery: Signal<PaymentsListQuery> = this.store.selectSignal(PaymentsState.allPaymentsTableQuery);

	/** Current query for the "Ready for Review" table */
	readonly readyForReviewTableQuery: Signal<PaymentsListQuery> = this.store.selectSignal(PaymentsState.readyForReviewTableQuery);

	/** Current query for the "Open Payments" table */
	readonly openPaymentsTableQuery: Signal<PaymentsListQuery> = this.store.selectSignal(PaymentsState.openPaymentsTableQuery);

	/** Total count of records matching "All Payments" table query */
	readonly allPaymentsTableTotalCount: Signal<number> = this.store.selectSignal(PaymentsState.allPaymentsTableTotalCount);

	/** Total count of records matching "Ready for Review" table query */
	readonly readyForReviewTableTotalCount: Signal<number> = this.store.selectSignal(PaymentsState.readyForReviewTableTotalCount);

	/** Total count of records matching "Open Payments" table query */
	readonly openPaymentsTableTotalCount: Signal<number> = this.store.selectSignal(PaymentsState.openPaymentsTableTotalCount);

	/** Indicates whether "All Payments" table data is currently loading */
	readonly isAllPaymentsLoading: Signal<boolean> = this.store.selectSignal(PaymentsState.isAllPaymentsLoading);

	/** Indicates whether "Ready for Review" table data is currently loading */
	readonly isReadyForReviewLoading: Signal<boolean> = this.store.selectSignal(PaymentsState.isReadyForReviewLoading);

	/** Indicates whether "Open Payments" table data is currently loading */
	readonly isOpenPaymentsLoading: Signal<boolean> = this.store.selectSignal(PaymentsState.isOpenPaymentsLoading);

	/** Indicates whether review notification cards (snackbar alerts) are currently loading */
	readonly areSnacksLoading: Signal<boolean> = this.store.selectSignal(PaymentsState.areSnacksLoading);

	/** Array of batch IDs currently loading child payments */
	readonly batchIdsLoadingChildren: Signal<string[]> = this.store.selectSignal(PaymentsState.batchIdsLoadingChildren);

	/** Array of batch IDs where child payment fetching failed */
	readonly batchIdsWithChildLoadError: Signal<string[]> = this.store.selectSignal(PaymentsState.batchIdsWithChildLoadError);

	/** "All Payments" table records (includes all statuses, payments and batches) */
	readonly allPaymentsTableRecords: Signal<PaymentRecord[]> = this.store.selectSignal(PaymentsState.allPaymentsTableRecords);

	/** "Ready for Review" table records that need review */
	readonly reviewPaymentsTableRecords: Signal<PaymentRecord[]> = this.store.selectSignal(PaymentsState.reviewPaymentsTableRecords);

	/** "Open Payments" table records (OPEN status only) */
	readonly openPaymentsTableRecords: Signal<PaymentRecord[]> = this.store.selectSignal(PaymentsState.openPaymentsTableRecords);

	/** Snackbar notification cards for payments needing review/approval */
	readonly paymentsSnacks: Signal<ReadyForReviewSnack[]> = this.store.selectSignal(PaymentsState.paymentsSnacks);

	// ==========================================
	// Computed Signals
	// ==========================================

	/**
	 * Indicates whether any payment in the store requires review by the current user.
	 * Checks all payment entities across all tabs.
	 */
	private readonly hasAnyReviewablePayments: Signal<boolean> = computed(() => {
		const entities: PaymentRecord[] = this.paymentRecordEntities();
		return entities?.some((record: PaymentRecord) => record.needsReview === true) ?? false;
	});

	/**
	 * Indicates if the current user can review payments.
	 * Returns true if user is in active workflow stages OR has any payments assigned for review.
	 * Returns null while loading (workflows haven't loaded yet and no reviewable payments found).
	 * Returns false when confirmed no access.
	 *
	 * Used to:
	 * - Show/hide the "Ready for Review" tab
	 * - Show/hide the multi-select toolbar in all tabs
	 * - Enable/disable row selection checkboxes
	 */
	readonly canReviewPayments: Signal<boolean | null> = computed(() => {
		const isInWorkflows: boolean | null = this.workflowsFacade.isUserInActiveWorkflowStages();
		const hasReviewable: boolean = this.hasAnyReviewablePayments();

		// If user has reviewable payments, show immediately (short-circuit loading)
		if (hasReviewable) {
			return true;
		}

		// If user is confirmed in workflows, show
		if (isInWorkflows === true) {
			return true;
		}

		// If still loading workflows (and no reviewable payments found yet), return null
		if (isInWorkflows === null) {
			return null;
		}

		// Done loading, no reviewable payments, not in workflows
		return false;
	});

	// ==========================================
	// Store Observables (private, for internal async operations)
	// ==========================================

	/**
	 * Why observables alongside signals?
	 * Signals are synchronous and ideal for template binding. Observables are needed
	 * when we require RxJS operators (firstValueFrom, lastValueFrom) for Promise-based
	 * async methods that wait for state updates after dispatching actions.
	 */

	/** Observable for async tab operations (Promise-based methods) */
	private readonly selectedTab$: Observable<PaymentsTab> = this.store.select(PaymentsState.selectedTab);
	/** Used internally for institution instructions caching */
	private readonly institutionInstructionsMap$: Observable<InstitutionLevelInstructions> = this.store.select(InstructionsState.institutionInstructionsMap);
	/** Used internally for async payment record operations */
	private readonly paymentRecordEntities$: Observable<PaymentRecord[]> = this.store.select(PaymentsState.paymentRecordEntities);
	/** Used internally for async "All Payments" table operations */
	private readonly allPaymentsTabRecords$: Observable<PaymentRecord[]> = this.store.select(PaymentsState.allPaymentsTableRecords);
	/** Used internally for async "Open Payments" table operations */
	private readonly openPaymentsTabRecords$: Observable<PaymentRecord[]> = this.store.select(PaymentsState.openPaymentsTableRecords);
	/** Used internally for async "Ready for Review" table operations */
	private readonly reviewPaymentsTabRecords$: Observable<PaymentRecord[]> = this.store.select(PaymentsState.reviewPaymentsTableRecords);
	/** Used internally for filtering Payment records from mixed PaymentRecord arrays */
	private readonly allPaymentTypeRecords$: Observable<Payment[]> = this.paymentRecordEntities$.pipe(
		rxMap((records: PaymentRecord[]) => records.filter(isPayment))
	);

	// ==========================================
	// Snapshot Methods (synchronous store reads)
	// ==========================================

	/**
	 * Gets a single payment from store by ID without API call.
	 *
	 * @param paymentId - The payment ID to look up
	 * @returns Payment if found and is Payment type, undefined otherwise
	 */
	getPaymentFromStore(paymentId: string): Payment | undefined {
		const payment: PaymentRecord | undefined = this.store.selectSnapshot(PaymentsState.getPaymentRecordById)(paymentId);

		if (payment && isPayment(payment)) {
			return payment;
		}

		return undefined;
	}

	/**
	 * Gets a batch payment from store by ID without API call.
	 *
	 * @param batchPaymentId - The batch payment ID to look up
	 * @returns BatchPayment if found and is BatchPayment type, undefined otherwise
	 */
	getBatchPaymentFromStore(batchPaymentId: string): BatchPayment | undefined {
		const batchPayment: PaymentRecord | undefined = this.store.selectSnapshot(PaymentsState.getPaymentRecordById)(batchPaymentId);

		if (batchPayment && isBatchPayment(batchPayment)) {
			return batchPayment;
		}

		return undefined;
	}

	/**
	 * Gets all Payment-type records from store asynchronously.
	 *
	 * @returns Promise resolving to array of Payment records
	 */
	async getAllPaymentsFromStore(): Promise<Payment[]> {
		return firstValidValueFrom(this.allPaymentTypeRecords$);
	}

	// ==========================================
	// Tab & Navigation Methods
	// ==========================================

	/**
	 * Sets the selected payments tab and returns the updated tab.
	 *
	 * @param selectedTab - The tab to select
	 * @returns Promise resolving to the newly selected tab
	 */
	async setSelectedTab(selectedTab: PaymentsTab): Promise<PaymentsTab> {
		await lastValueFrom(this.store.dispatch(new SetPaymentsTabIndex(selectedTab)));
		return firstValidValueFrom(this.selectedTab$);
	}

	// ==========================================
	// Payment Record API Methods
	// ==========================================

	/**
	 * Fetches "All Payments" table records from API and updates store.
	 *
	 * @param filters - Query parameters for filtering, pagination, and sorting
	 * @returns Promise resolving to fetched payment records
	 */
	async getAllPaymentsTableRecordsFromApi(filters: PaymentsListQuery): Promise<PaymentRecord[]> {
		await lastValueFrom(this.store.dispatch(new GetPaymentRecords(filters, PaymentsTab.allPayments)));
		return firstValidValueFrom(this.allPaymentsTabRecords$);
	}

	/**
	 * Fetches "Ready for Review" table records from API and updates store.
	 *
	 * @param filters - Query parameters for filtering, pagination, and sorting
	 * @returns Promise resolving to fetched payment records
	 */
	async getReadyForReviewPaymentsTableRecordsFromApi(filters: PaymentsListQuery): Promise<PaymentRecord[]> {
		await lastValueFrom(this.store.dispatch(new GetPaymentRecords(filters, PaymentsTab.readyForReview)));
		return firstValidValueFrom(this.reviewPaymentsTabRecords$);
	}

	/**
	 * Fetches "Open Payments" table records from API and updates store.
	 *
	 * @param filters - Query parameters for filtering, pagination, and sorting
	 * @returns Promise resolving to fetched payment records
	 */
	async getOpenPaymentsTableRecordsFromApi(filters: PaymentsListQuery): Promise<PaymentRecord[]> {
		await lastValueFrom(this.store.dispatch(new GetPaymentRecords(filters, PaymentsTab.openPayments)));
		return firstValidValueFrom(this.openPaymentsTabRecords$);
	}

	/**
	 * Fetches a single payment from API by ID and updates store.
	 *
	 * @param paymentId - The payment ID to fetch
	 * @returns Promise resolving to the Payment, or undefined if not found
	 */
	async getPaymentFromApi(paymentId: string): Promise<Payment | undefined> {
		await lastValueFrom(this.store.dispatch(new GetPaymentById(paymentId)));
		return this.getPaymentFromStore(paymentId);
	}

	/**
	 * Fetches a batch payment from API by ID and updates store.
	 *
	 * @param batchPaymentId - The batch payment ID to fetch
	 * @returns Promise resolving to the BatchPayment, or undefined if not found
	 */
	async getBatchPaymentFromApi(batchPaymentId: string): Promise<BatchPayment | undefined> {
		await lastValueFrom(this.store.dispatch(new GetBatchPaymentById(batchPaymentId)));
		return this.getBatchPaymentFromStore(batchPaymentId);
	}

	// ==========================================
	// Payment CRUD Methods
	// ==========================================

	/**
	 * Cancels a payment and returns the updated payment from store.
	 *
	 * @param paymentCancelation - Contains paymentId of payment to cancel
	 * @returns Updated Payment entity from store, or undefined if not found
	 */
	async cancelPayment(paymentCancelation: PaymentSubmissionOrCancelation): Promise<Payment | undefined> {
		await lastValueFrom(this.store.dispatch(new CancelPayment(paymentCancelation)));
		return this.getPaymentFromStore(paymentCancelation.paymentId);
	}

	/**
	 * Sets stage feedback (approve/reject) on a payment.
	 *
	 * @param stageFeedback - Feedback containing paymentId and approval decision
	 * @returns Updated Payment entity from store, or undefined if not found
	 */
	async setStageFeedback(stageFeedback: StageFeedback): Promise<Payment | undefined> {
		await lastValueFrom(this.store.dispatch(new SetStageFeedback(stageFeedback)));
		return this.getPaymentFromStore(stageFeedback.paymentId);
	}

	// ==========================================
	// Batch Payment Methods
	// ==========================================

	/**
	 * Cancels a batch payment and returns the updated batch from store.
	 *
	 * @param batchPaymentCancelation - Contains batchId of batch payment to cancel
	 * @returns Updated BatchPayment entity from store, or undefined if not found
	 */
	async cancelBatchPayment(batchPaymentCancelation: PaymentSubmissionOrCancelation): Promise<BatchPayment | undefined> {
		await lastValueFrom(this.store.dispatch(new CancelBatchPayment(batchPaymentCancelation)));
		return this.getBatchPaymentFromStore(batchPaymentCancelation.batchId);
	}

	/**
	 * Sets stage feedback (approve/reject) on a batch payment.
	 *
	 * @param stageFeedback - Feedback containing batchId and approval decision
	 * @returns Updated BatchPayment entity from store, or undefined if not found
	 */
	async setBatchStageFeedback(stageFeedback: StageFeedback): Promise<BatchPayment | undefined> {
		await lastValueFrom(this.store.dispatch(new SetBatchStageFeedback(stageFeedback)));
		return this.getBatchPaymentFromStore(stageFeedback.batchId);
	}

	/**
	 * Fetches child payments for a batch directly from API (bypasses store).
	 *
	 * @param batchId - The batch ID to fetch children for
	 * @returns Promise resolving to array of child payments
	 */
	async getPaymentsByBatchId(batchId: string): Promise<Payment[]> {
		const response: GetPaymentsV1Response = await lastValueFrom(this.paymentsHttpService.getPaymentsByBatchId(batchId));
		return response.payments;
	}

	/**
	 * Creates a new batch payment from form data.
	 *
	 * @param formData - Form data containing batch payment details
	 */
	async createBatch(formData: FormData): Promise<void> {
		await lastValueFrom(this.store.dispatch(new CreateBatchPayment(formData)));
	}

	/**
	 * Validates an uploaded batch payment CSV file before submission.
	 *
	 * Wraps the HTTP call directly rather than dispatching an NGXS action because the response
	 * is consumed transiently by the Import Payments dialog and never persisted in shared state.
	 *
	 * @param formData - Form data containing the CSV file under the `file` key
	 * @returns Promise resolving to the validation response (parsed payments + any errors)
	 */
	async validateBatchCsvFile(formData: FormData): Promise<ValidateBatchCsvFileResponse> {
		return lastValueFrom(this.paymentsHttpService.validateBatchCsvFile(formData));
	}

	/**
	 * Creates a new payment and tracks analytics.
	 *
	 * @param paymentToCreate - Payment data for creation
	 */
	async createPayment(paymentToCreate: PaymentToCreate): Promise<void> {
		await lastValueFrom(this.store.dispatch(new CreatePayment(paymentToCreate)));
		this.vitallyService.userActionWithParams(AnalyticsUserActions.paymentsInitiate, {
			name: paymentToCreate.name,
			paymentType: paymentToCreate.type,
			accountId: paymentToCreate.accountId,
			country: paymentToCreate.country,
			beneficiaryAccountId: paymentToCreate.beneficiaryAccountId,
			templateId: paymentToCreate.templateId,
		});
	}

	// ==========================================
	// Bulk Operations
	// ==========================================

	/**
	 * Submits bulk approval/rejection for multiple payments and batches.
	 *
	 * This method calls the service directly to get the full response
	 * (including errors). State is updated via UpdatePaymentsFromBulkApproval action
	 * for successful payments only.
	 *
	 * Note: We call the service directly instead of dispatching SubmitBulkApproval
	 * because NGXS dispatch returns Observable<void>, not the action handler's return value.
	 * After getting the response, we dispatch UpdatePaymentsFromBulkApproval to update state.
	 *
	 * @param items - Array of approval request items
	 * @param otp - Optional 6-digit OTP for MFA verification (required for Releaser stage when MFA enabled)
	 * @param refreshToken - Optional Auth0 refresh token for MFA verification
	 * @returns Categorized bulk approval results
	 */
	async submitBulkApproval(items: readonly BulkApprovalRequestItem[], otp?: string, refreshToken?: string): Promise<BulkApprovalResult> {
		const request: BulkApprovalRequest = {
			approvals: items,
			...(otp && { otp }),
			...(refreshToken && { refreshToken }),
		};
		const response: BulkApprovalResponse = await lastValueFrom(this.paymentsHttpService.submitBulkApproval(request));

		if (response.payments?.length > 0) {
			await lastValueFrom(this.store.dispatch(new UpdatePaymentsFromBulkApproval(response.payments)));
		}

		return this.categorizeBulkApprovalResults(response, items);
	}

	// ==========================================
	// File Operations
	// ==========================================

	/**
	 * Deletes a file attachment from a payment.
	 *
	 * @param attachmentId - The attachment ID to delete
	 * @param paymentId - The payment ID the attachment belongs to
	 */
	async deleteFileFromPayment(attachmentId: string, paymentId: string): Promise<void> {
		await lastValueFrom(this.store.dispatch(new DeleteFileFromPayment(attachmentId, paymentId)));
	}

	/**
	 * Uploads multiple files to a payment sequentially.
	 *
	 * Files are uploaded one at a time using RxJS `concat()` to prevent
	 * race conditions and ensure consistent server-side ordering.
	 * If any upload fails, the operation rejects immediately and
	 * remaining files are not uploaded.
	 *
	 * @param paymentFiles - Array of files to upload
	 * @param paymentId - The payment ID to attach files to
	 */
	uploadFilesToPayment(paymentFiles: readonly File[], paymentId: string): Promise<void> {
		return new Promise((resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: Error) => void) => {
			const observablesToAwait: Observable<void>[] = paymentFiles.map((file: File) => {
				const body: FormData = new FormData();
				body.append('paymentFile', file);
				return this.store.dispatch(new UploadFileToPayment(body, paymentId));
			});
			concat(...observablesToAwait).subscribe({
				complete: () => {
					resolve();
				},
				error: (error: Error) => {
					reject(error);
				},
			});
		});
	}

	// ==========================================
	// External API Methods (bank info, instructions)
	// ==========================================

	/**
	 * Fetches bank information from LexisNexis by account number or BIC.
	 *
	 * @param isNumberbic - True if looking up by BIC (Bank Identifier Code/SWIFT code), false for account number
	 * @param accountNumberOrBic - The account number or BIC to look up
	 * @param countryCode - The country code for the lookup
	 * @returns Bank information response
	 */
	async getBankInformation(isNumberbic: boolean, accountNumberOrBic: string, countryCode: string): Promise<LexisNexisBankInformationResponse> {
		return lastValueFrom(this.paymentsHttpService.getBankInformationByAccountNumber(isNumberbic, accountNumberOrBic, countryCode));
	}

	/**
	 * Gets institution-specific payment instructions with caching.
	 * Checks store cache first, fetches from API if not found.
	 *
	 * @param countryCode - The country code for instructions
	 * @param institution - The financial institution identifier
	 * @param paymentType - The type of payment
	 * @returns Array of additional instructions, or empty array if none found
	 */
	async getInstitutionInstructions(countryCode: string, institution: string, paymentType: string): Promise<InstitutionAddtlInstruction[]> {
		// firstValueFrom: returns immediate store value (may be null/empty)
		// firstValidValueFrom: waits for truthy value (use after dispatch when data is expected)
		let map: InstitutionLevelInstructions = await firstValueFrom(this.institutionInstructionsMap$);

		if (map?.[institution]?.[paymentType]?.instructions?.[countryCode]) {
			return map[institution][paymentType].instructions[countryCode];
		}

		await firstValueFrom(this.store.dispatch(new GetInstitutionInstructions(countryCode, institution, paymentType)));

		map = await firstValidValueFrom(this.institutionInstructionsMap$);
		return map?.[institution]?.[paymentType]?.instructions?.[countryCode] ?? [];
	}

	/**
	 * Gets available countries for an institution and payment type with caching.
	 * Checks store cache first, fetches from API if not found.
	 *
	 * @param institution - The financial institution identifier
	 * @param paymentType - The type of payment
	 * @returns Array of available countries, or empty array if none found
	 */
	async getInstitutionCountries(institution: string, paymentType: string): Promise<InstitutionCountry[]> {
		let map: InstitutionLevelInstructions = await firstValueFrom(this.institutionInstructionsMap$);

		if (map?.[institution]?.[paymentType]?.availableCountries) {
			return map[institution][paymentType].availableCountries;
		}

		await firstValueFrom(this.store.dispatch(new GetInstitutionCountries(institution, paymentType)));

		map = await firstValidValueFrom(this.institutionInstructionsMap$);
		return map?.[institution]?.[paymentType]?.availableCountries ?? [];
	}

	// ==========================================
	// Private Methods
	// ==========================================

	/**
	 * Categorizes bulk approval results into success/failure buckets for UI consumption.
	 *
	 * Separates successful payments from batches, calculates totals, and determines
	 * overall status flags (allSucceeded, allFailed, hasPartialSuccess) for
	 * appropriate user feedback messaging.
	 */
	private categorizeBulkApprovalResults(response: BulkApprovalResponse, requestItems: readonly BulkApprovalRequestItem[]): BulkApprovalResult {
		const successfulPayments: Payment[] = response.payments.filter(isPayment);
		const successfulBatches: BatchPayment[] = response.payments.filter(isBatchPayment);
		const totalRequested: number = requestItems.length;
		const totalSucceeded: number = response.payments.length;
		const totalFailed: number = response.errors.length;

		return {
			response,
			successfulPayments,
			successfulBatches,
			failedItems: response.errors,
			hasPartialSuccess: totalSucceeded > 0 && totalFailed > 0,
			allSucceeded: totalSucceeded === totalRequested && totalFailed === 0,
			allFailed: totalSucceeded === 0 && totalFailed > 0,
		};
	}
}
